import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { resolvedFilingThreadSchema } from "./resolve-collection-filing-target.step";

/**
 * Match Collection Threads Step
 *
 * The content-matching counterpart to resolve_collection_filing_target. Where
 * that step trusts a rep's explicit COL-N instruction, this one attributes an
 * ARBITRARY inbound email to the sender-customer's open collection threads by
 * content — resolving the customer, scanning invoice/PO/SO identifiers, and
 * LLM-ranking the rest.
 *
 * Emits only the high-confidence (auto-file) band as `threads`, shaped to feed
 * straight into file_collection_evidence (the node downstream sets
 * filedBy=auto_match; with no actor wired, the file step files as the system).
 * Lower-confidence matches are counted in `suggestedCount` but not emitted —
 * they are intentionally not auto-filed.
 */
export const matchCollectionThreadsStep = defineStepConfig({
  type: "match_collection_threads",
  name: "Match Collection Threads",
  description:
    "Matches an inbound email to a customer's open collection threads by content; emits the high-confidence matches to file",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to match against open collection threads",
    ),
  }),

  outputSchema: z.object({
    customerId: z
      .uuid()
      .nullable()
      .describe("Customer the email was attributed to, or null if unresolved"),
    customerName: z.string().nullable(),
    threads: z
      .array(resolvedFilingThreadSchema)
      .describe(
        "High-confidence matched threads, ready to feed file_collection_evidence",
      ),
    filedBy: z
      .enum(["email", "auto_match"])
      .describe(
        "Always 'auto_match' — wired into file_collection_evidence so the system filing is tagged correctly",
      ),
    suggestedCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Lower-confidence matches surfaced for review (not emitted)"),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
